import React, {useContext, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';


import {SelectionHeader, SelectionRow} from 'src/components/data-table-selection/components';
import {clearSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {UserLink} from 'src/components/links';
import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ToolsRibbonComponent} from 'src/components/tools-ribbon';
import {NO_RESULTS_MESSAGE} from 'src/constants';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {isDeepEqual} from 'src/utils/isDeepEqual';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Cell, DataTable, TableHeader} from '@pb/kit-component-data-table-v2';
import DiffTime from '@pb/kit-component-diff-time';
import {Dropdown} from '@pb/kit-component-dropdown';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {FormattedDate} from '@pb/kit-component-formatters';
import {LocaleContext} from '@pb/kit-context-locale';

import {
    GroupNameHeader,
    GroupsPagination,
    MembersCell,
    MembersHeader,
} from './components';
import {COLUMNS, COLUMNS_DATA, HEADERS, PLACE, SELECTION_KEY} from './constants';
import {useGetGroupsTab} from './hooks/useGetGroupsTab';
import {groupsTabMembersActions} from './slices/groups-tab-members.slice';
import {groupsTabStateActions} from './slices/groups-tab-state.slice';
import {groupsTabActions} from './slices/groups-tab.slice';

import type {CellOptions, Column, HeaderCellOptions, RowsParams} from '@pb/kit-component-data-table-v2';


type Props = {
    onSelectGroup: (selectedGroupId: number) => void,
    onSelectGroupMembers: (selectedGroupId: number) => void,
};

type GroupNameValue = {
    id: number,
    name: string,
};

type GroupOwnerValue = {
    userId: number,
    email: string,
};

export const AccountGroupsTable: React.FC<Props> = props => {
    const {onSelectGroup, onSelectGroupMembers} = props;
    const dispatch = useDispatch();

    const {locale} = useContext(LocaleContext);
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const {
        result,
        loadingError,
        pagination,
        cacheKeys,
    } = useGetGroupsTab();

    const rows = useMemo(() => (Array.isArray(result?.data) ? result?.data.map(group => ({
        id: group.groupId,
        columnValues: {
            groupName: {id: group.groupId, name: group.groupName},
            members: '',
            groupOwner: group.ownerInfo,
            notes: group.notes,
            lastUpdateDate: group.lastUpdateDate,
        },
    })) : undefined), [result?.data]);

    const maxRowNumber = getPaginatedRowNumber({...pagination, index: rows?.length});
    const entityIds = useMemo(() => result?.data.map(({groupId}) => groupId) || [], [result]);
    const totalCount = result?.totalCount || 0;

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(groupsTabActions.clearData());
            dispatch(groupsTabMembersActions.clearData());
            dispatch(groupsTabStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            dispatch(clearSelection(SELECTION_KEY));
        }
    }, cacheKeys.filters);

    return (
        <BoxLoading loading={!result}>
            <ServerResponseHandler serverError={loadingError}>
                <>
                    <ToolsRibbonComponent {...{rows: entityIds, selectionKey: SELECTION_KEY, totalCount}}/>
                    <DataTable
                        onStopResize={onStopResize}
                        data={{rows, columns: COLUMNS_DATA.map((column: Column) => ({
                            ...column,
                            width: getColumnWidthByColumnId(column.columnId),
                        }))}}
                        headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                            switch (column.columnId) {
                                case COLUMNS.GROUP_NAME:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <MainColumnHeader
                                                selection={(
                                                    <SelectionHeader
                                                        selectionKey={SELECTION_KEY}
                                                        entityIds={entityIds}
                                                        totalCount={result?.totalCount || 0}
                                                    />
                                                )}
                                                maxRowNumber={maxRowNumber}>
                                                <GroupNameHeader/>
                                            </MainColumnHeader>
                                        </Cell>
                                    );
                                case COLUMNS.MEMBERS:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <TableHeader indent="none" className="search-header">
                                                <MembersHeader/>
                                            </TableHeader>
                                        </Cell>
                                    );
                                default:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <TableHeader indent="none" className="search-header">
                                                <strong>{HEADERS[column.columnId]}</strong>
                                            </TableHeader>
                                        </Cell>
                                    );
                            }
                        }}
                        renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                            const {rowIndex, column: {columnId}} = rowsParams;
                            const value = rowsParams.row.columnValues[columnId];
                            const rowId = +rowsParams.row.id;
                            const column = rowsParams.column;
                            const cellKey = rowIndex + columnId.toString();
                            const rowNumber = getPaginatedRowNumber({
                                ...pagination,
                                index: rowIndex + 1,
                            });

                            switch (columnId) {
                                case COLUMNS.GROUP_NAME:
                                    const group = value as GroupNameValue;
                                    return (
                                        <MainColumnCell
                                            key={cellKey}
                                            {...{
                                                selection: (
                                                    <SelectionRow
                                                        rows={entityIds}
                                                        index={rowIndex}
                                                        entityId={rowId}
                                                        selectionKey={SELECTION_KEY}
                                                        totalCount={totalCount}
                                                    />
                                                ),
                                                rowId,
                                                maxRowNumber,
                                                rowNumber,
                                                cellOptions: {...cellOptions, column},
                                            }}>
                                            <a onClick={() => onSelectGroup(group.id)}>
                                                <EllipsisTextHint title={group.name}>
                                                    {group.name}
                                                </EllipsisTextHint>
                                            </a>
                                        </MainColumnCell>
                                    );
                                case COLUMNS.MEMBERS:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={cellKey}
                                            column={column}
                                            className="search-result-cell">
                                            <MembersCell
                                                groupId={rowId}
                                                onSelectGroupMembers={onSelectGroupMembers}
                                            />
                                        </Cell>
                                    );
                                case COLUMNS.GROUP_OWNER:
                                    const owner = value as GroupOwnerValue;
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            column={column}
                                            key={cellKey}
                                            className="search-result-cell">
                                            <UserLink userId={owner.userId} caption={owner.email} place={PLACE}/>
                                        </Cell>
                                    );
                                case COLUMNS.NOTES:
                                    const note = value == null ? '' : String(value);
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            column={column}
                                            key={cellKey}
                                            className="search-result-cell">
                                            <Dropdown
                                                maxWidth={600}
                                                placement="bottom-start"
                                                hoverEnabled={true}
                                                anchor={({anchorRef, getAnchorProps}) => (
                                                    <span ref={anchorRef} {...getAnchorProps()}>
                                                        <span className="ellipsis">{note}</span>
                                                    </span>
                                                )}>
                                                <span>{note}</span>
                                            </Dropdown>
                                        </Cell>
                                    );
                                case COLUMNS.LAST_UPDATE:
                                    const lastUpdate = value as string | undefined;
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            column={column}
                                            key={cellKey}
                                            className="search-result-cell">
                                            {lastUpdate ? (
                                                <span
                                                    title={FormattedDate.format({
                                                        value: lastUpdate,
                                                        format: mapDateFormat(
                                                            locale.dateFormat,
                                                            'NUMERIC',
                                                            'DATE_TIME',
                                                        ),
                                                    }) || ''}>
                                                    <DiffTime
                                                        key={lastUpdate}
                                                        createdDate={lastUpdate}
                                                        fullUnits={true}
                                                    />
                                                </span>
                                            ) : '--'}
                                        </Cell>
                                    );
                                default:
                                    const displayValue = value !== -1 ? String(value) : 'N/A';
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={cellKey}
                                            column={column}
                                            className="search-result-cell">
                                            {displayValue}
                                        </Cell>
                                    );
                            }
                        }}
                        paginationStripe={<GroupsPagination totalCount={result?.totalCount || 0}/>}
                        showNoDataMsg={!result?.data?.length}
                        tableMessage={NO_RESULTS_MESSAGE}
                    />
                </>
            </ServerResponseHandler>
        </BoxLoading>
    );
};
